/* eslint-disable no-useless-escape */
// dependencies
import { useEffect, useState } from "react";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import toast, { Toaster } from "react-hot-toast";
import axios from "axios";
import Context from "./scripts/context/User";
import { STEPS } from "./layouts/Register/constants";
import { Box } from "@mui/system";
import { CircularProgress } from "@mui/material";
import { Helmet } from "react-helmet";

// components
import Navbar from "./components/Navbar";
import { WalletProvider } from "./scripts/context/WalletContext";

// Register Steps
import {
  PersonalInformation,
  EmailVerification,
  Disclaimer,
  UploadKYC,
  LinkWallet,
  CaseStatus,
} from "./components/RegisterSteps";

// pages
import {
  Login,
  ResetPassword,
  Profile,
  NotFound,
  HelpCenter,
  Balance,
  Purchase,
  Commission,
  LPStake,
} from "./pages";

// Layouts
import RegisterLayout from "./layouts/Register";
import Wallet from "./layouts/Wallet";

import CacheBuster from "./scripts/CacheBuster";

// styles
import GlobalStyles from "./styles/globalStyles";

export default function App() {
  const navigate = useNavigate();
  const [state, setState] = useState({
    user: null,
    redirect_uri: "",
    registration_step: 0,
  });
  const [theme, setTheme] = useState(localStorage.theme || "dark");

  const getRegistrationStep = (redirect_uri) =>
    STEPS.findIndex((step) => step.path === redirect_uri);

  const [getUserDataLoading, setGetUserDataLoading] = useState(
    !!localStorage.getItem("token")
  );

  function isMobileDevice() {
    return "ontouchstart" in window || "onmsgesturechange" in window;
  }

  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  axios.defaults.baseURL = process.env.REACT_APP_API_URL;
  axios.defaults.headers.common["Authorization"] =
    token && token.includes("Bearer") ? token : `Bearer ${token}`;

  console.warn = () => {};
  console.error = () => {};

  useEffect(() => {
    if (token) {
      setGetUserDataLoading(true);
      axios
        .get("/getUserData")
        .then(({ data }) => {
          setState({
            ...state,
            user: data.user,
            redirect_uri: data.redirect_uri,
            registration_step: getRegistrationStep(data.redirect_uri),
          });
          setGetUserDataLoading(false);
          data.redirect_uri.startsWith("/register") &&
            !data.redirect_uri.startsWith("/filesystem") &&
            navigate(data.redirect_uri);
        })
        .catch(({ response }) => {
          setGetUserDataLoading(false);
          toast.error(response.data.message);
          if (response.status === 401) {
            localStorage.removeItem("token");
            sessionStorage.removeItem("token");
            navigate("/login");
          }
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUserData = () => {
    setGetUserDataLoading(true);

    if (token) {
      axios
        .get("/getUserData")
        .then(({ data }) => {
          setState({
            ...state,
            user: {
              ...data.user,
            },
            redirect_uri: data.redirect_uri,
            registration_step: getRegistrationStep(data.redirect_uri),
          });
          setGetUserDataLoading(false);
        })
        .catch(({ response }) => {
          if (response.status === 401) {
            localStorage.removeItem("token");
            sessionStorage.removeItem("token");
            navigate("/login");
          }
        });
    }
  };
  const darkThemeMq = window.matchMedia("(prefers-color-scheme: dark)");

  if (getUserDataLoading)
    return (
      <div className="d-flex flex-column flex-root">
        <GlobalStyles theme={theme} thereIsNavbar={false} />
        <div className="login login-3 login-signin-on d-flex flex-row-fluid">
          <div className="d-flex flex-center bgi-size-cover bgi-no-repeat flex-row-fluid">
            <Box
              sx={{
                alignItems: "center",
              }}
            >
              <GlobalStyles theme={theme} />
              <CircularProgress />
            </Box>
          </div>
        </div>
      </div>
    );

  if (!window.ethereum && isMobileDevice()) {
    return (
      <div className="d-flex flex-column flex-root">
        <GlobalStyles theme={theme} thereIsNavbar={false} />
        <div className="login login-3 login-signin-on d-flex flex-row-fluid">
          <div
            className="d-flex flex-center flex-column bgi-size-cover bgi-no-repeat flex-row-fluid"
            style={{
              height: "100vh",
            }}
          >
            <button
              className="btn btn-pill btn-primary verify-btn font-weight-bold px-15 py-3"
              onClick={() => {
                window.open(
                  `https://metamask.app.link/dapp/${
                    window.location.origin.split("//")[1]
                  }`
                );
              }}
            >
              Open MetaMask
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CacheBuster>
      {({ loading, isLatestVersion, refreshCacheAndReload }) => {
        if (!loading && !isLatestVersion) {
          // You can decide how and when you want to force reload
          refreshCacheAndReload();
        }
        return (
          <>
            <Helmet>
              {darkThemeMq.matches ? (
                <link rel="icon" href="/favicon.ico" />
              ) : (
                <link rel="icon" href="/Image20220220123515.png" />
              )}
            </Helmet>
            <Context.Provider
              value={{
                globalState: state,
                setGlobalState: (newObject) =>
                  setState({ ...state, ...newObject }),
                getRegistrationStep,
                getUserData,
                setGetUserDataLoading,
              }}
            >
              <WalletProvider>
                <ThemeProvider theme={{ theme }}>
                  <Toaster position="top-right" reverseOrder={false} />
                  {state.user?.status && !(state.registration_step >= 0) && (
                    <Navbar
                      handleToggleThemes={() => {
                        setTheme(theme === "light" ? "dark" : "light");
                        localStorage.setItem(
                          "theme",
                          theme === "light" ? "dark" : "light"
                        );
                      }}
                      isDark={theme === "dark"}
                    />
                  )}
                  <Box
                    className={theme === "light" ? "" : "dark-mode"}
                    sx={{
                      justifyContent: "flex-start !important",
                    }}
                  >
                    <GlobalStyles
                      theme={theme}
                      thereIsNavbar={state.user?.status === "Active"}
                    />
                    <Routes>
                      <Route
                        index
                        path="/"
                        element={<Navigate to="/login" />}
                      />
                      <Route path="login" element={<Login />} />
                      <Route
                        path="password/reset"
                        element={<ResetPassword />}
                      />
                      <Route path="profile" element={<Profile />} />
                      <Route
                        path="register"
                        element={
                          state.user?.status === "Active" ? (
                            <Navigate to={state.redirect_uri || "/login"} />
                          ) : (
                            <RegisterLayout />
                          )
                        }
                      >
                        <Route
                          path="personal-information"
                          element={<PersonalInformation />}
                        />
                        <Route
                          path="email-verification"
                          element={<EmailVerification />}
                        />
                        <Route path="disclaimer" element={<Disclaimer />} />
                        <Route path="upload-kyc" element={<UploadKYC />} />
                        <Route path="link-wallet" element={<LinkWallet />} />
                        <Route
                          path="account-summary"
                          element={<CaseStatus />}
                        />
                        <Route path="*" element={<NotFound />} />
                      </Route>
                      <Route
                        path="wallet"
                        element={
                          state.user?.status === "Active" ? (
                            <Wallet />
                          ) : (
                            <Navigate to={state.redirect_uri || "/login"} />
                          )
                        }
                      >
                        <Route path="balance" element={<Balance />} />
                        <Route path="purchase" element={<Purchase />} />
                        {/* <Route path="stake" element={<LPStake />} /> */}
                        {state.user?.invitation_code && (
                          <Route path="commission" element={<Commission />} />
                        )}
                        <Route path="*" element={<NotFound />} />
                      </Route>
                      <Route path="tickets" element={<HelpCenter />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Box>
                </ThemeProvider>
              </WalletProvider>
            </Context.Provider>
            <div
              id="background-radial-gradient"
              style={{
                width: "200vw",
                height: "200vh",
                transform: "translate(-50vw, -100vh)",
              }}
            ></div>
          </>
        );
      }}
    </CacheBuster>
  );
}
